import React from 'react';
import isEmpty from 'lodash.isempty';
import Box from '@material-ui/core/Box';
import {withStyles} from '@material-ui/core';
import {useSelector} from 'react-redux';
import {Header, Footer, WarningBars} from '../components';
import {Affiliate} from '../../shared/types';
import {AccountInfo} from '../reducers/user.reducer';
import {TaxState} from '../reducers/tax';
import {RedirectWithParams} from 'epic-diesel-common';
import {isActiveAffiliate} from '../../shared/utils/ValidationUtils';
import {ConfigState} from '../reducers/config';

import {
    selectConfig,
    selectAffiliate,
    selectAccountInfo,
    selectTax,
    selectIsLoggedIn,
    selectMessages
} from '../selectors';

const ContentWrapper = withStyles(theme => ({
    root: {
        width: '90%',
        margin: '0 auto',
        minHeight: '60vh',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(12),
        maxWidth: '1920px'
    }
}))(Box);

export interface WithPageLayoutProps {
    messages: Record<string, string>;
    affiliateAccount: Affiliate;
    accountInfo: AccountInfo;
    configState: ConfigState;
    taxState?: TaxState;
}

export interface WithPageLayoutOptions {
    skipLoginCheck?: boolean;
    skipAffiliateStatusCheck?: boolean;
    skipEditSettingsCheck?: boolean;
}

export default function withPageLayout<TProps, TRefElement = any>(
    WrappedComponent: React.ComponentType<TProps & WithPageLayoutProps>,
    options?: WithPageLayoutOptions
) {
    const WithPageLayoutComp = React.forwardRef<TRefElement, TProps>((props, forwardRef) => {
        const messages = useSelector(selectMessages);
        const configState = useSelector(selectConfig);
        const affiliate = useSelector(selectAffiliate);
        const accountInfo = useSelector(selectAccountInfo);
        const taxState = useSelector(
            state => selectIsLoggedIn(state) && !isEmpty(affiliate) && selectTax(state)
        );

        const {
            skipLoginCheck = false,
            skipAffiliateStatusCheck = false,
            skipEditSettingsCheck = true
        } = options || {};

        const {isLoggedIn = false} = accountInfo || {};
        const {taxCountrySanctioned, taxInterviewUrl: isTakingTaxInterview} = taxState || {};
        const {canEditSettings} = affiliate || {};

        const canViewPortal =
            isActiveAffiliate(affiliate) || (!skipEditSettingsCheck && canEditSettings);

        if (
            (!skipLoginCheck && !isLoggedIn) ||
            (!skipAffiliateStatusCheck && !canViewPortal) ||
            taxCountrySanctioned
        ) {
            return <RedirectWithParams to="overview" ignoreSearch />;
        }

        const showWarningBars = !isTakingTaxInterview;

        return (
            <>
                <Header
                    messages={messages}
                    affiliateAccount={affiliate}
                    accountInfo={accountInfo}
                    configState={configState}
                    taxState={taxState}
                />
                <ContentWrapper>
                    {showWarningBars && (
                        <WarningBars
                            key="alert"
                            messages={messages}
                            canViewAlerts={isLoggedIn && !taxCountrySanctioned && canViewPortal}
                        />
                    )}

                    <Box sx={{marginTop: '2em'}}>
                        <WrappedComponent
                            ref={forwardRef}
                            messages={messages}
                            affiliateAccount={affiliate}
                            accountInfo={accountInfo}
                            configState={configState}
                            taxState={taxState}
                            {...props}
                        />
                    </Box>
                </ContentWrapper>
                <Footer content={configState.epicFooter} accountInfo={accountInfo} />
            </>
        );
    });

    WithPageLayoutComp.displayName = `withPageLayout(${WrappedComponent.displayName ||
        WrappedComponent.name ||
        'Component'})`;

    return WithPageLayoutComp;
}
